import { useEffect, useState } from "react";
import api, { formatApiError, formatDate, formatMoney } from "@/lib/api";
import { toast } from "sonner";
import { PaperPlaneTilt } from "@phosphor-icons/react";

const STATUSES = ["new", "contacted", "quoted", "won", "lost"];

function PaymentLinkForm({ q, onDone, onCancel }) {
  const [amount, setAmount] = useState(q.quoted_amount || "");
  const [description, setDescription] = useState(q.quote_description || "");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/shop/quotes/${q.id}/send-payment-link`, {
        amount: parseFloat(amount), description, origin_url: window.location.origin,
      });
      toast.success("Payment link emailed to customer");
      navigator.clipboard?.writeText(data.pay_url).catch(() => {});
      onDone();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  return (
    <div className="mt-3 grid grid-cols-1 md:grid-cols-[160px_1fr_auto_auto] gap-2" data-testid={`admin-quote-link-form-${q.id}`}>
      <input type="number" step="0.01" min="0" placeholder="Amount $" value={amount} onChange={(e) => setAmount(e.target.value)} className="input-rugged" data-testid={`admin-quote-amount-${q.id}`} />
      <input placeholder="What's included (optional)" value={description} onChange={(e) => setDescription(e.target.value)} className="input-rugged" data-testid={`admin-quote-description-${q.id}`} />
      <button onClick={send} disabled={busy || !(parseFloat(amount) > 0)} className="btn-primary text-xs" data-testid={`admin-quote-send-link-${q.id}`}>
        <PaperPlaneTilt size={12} weight="bold" /> {busy ? "Sending..." : "Email payment link"}
      </button>
      <button onClick={onCancel} className="btn-secondary text-xs">Cancel</button>
    </div>
  );
}

function QuoteRow({ q, onSaved }) {
  const [linkOpen, setLinkOpen] = useState(false);
  const setStatus = async (status) => {
    try { await api.patch(`/admin/shop/quotes/${q.id}`, { status }); toast.success("Status updated"); onSaved(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="surface p-4" data-testid={`admin-quote-${q.id}`}>
      <div className="flex flex-col lg:flex-row lg:items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-chivo font-bold text-white">{q.product_name}</span>
            <span className={`tag ${q.status === "new" ? "tag-active" : ""}`} data-testid={`admin-quote-status-${q.id}`}>{q.status.toUpperCase()}</span>
            {q.paid && <span className="tag tag-active" data-testid={`admin-quote-paid-${q.id}`}>PAID {formatMoney(q.amount_paid)}</span>}
            {!q.paid && q.quoted_amount && <span className="tag">QUOTED {formatMoney(q.quoted_amount)}</span>}
            <span className="tag">{q.user_id ? "MEMBER" : "GUEST"}</span>
          </div>
          <div className="font-mono text-xs text-[#9CA3AF] mt-1">{formatDate(q.created_at)} · {q.name}{q.company ? ` · ${q.company}` : ""} · <a href={`mailto:${q.email}`} className="text-[#00D4FF]">{q.email}</a> · {q.phone || "—"} · zip {q.zip || "—"}</div>
          {q.message && <p className="font-plex text-sm text-[#9CA3AF] mt-2 whitespace-pre-wrap">{q.message}</p>}
          {q.pay_url && !q.paid && <div className="font-mono text-[10px] text-[#52525B] mt-1 break-all">Pay link: <a href={q.pay_url} target="_blank" rel="noreferrer" className="text-[#00D4FF]">{q.pay_url}</a></div>}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select value={q.status} onChange={(e) => setStatus(e.target.value)} className="input-rugged w-36" data-testid={`admin-quote-status-select-${q.id}`}>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          {!q.paid && !linkOpen && (
            <button onClick={() => setLinkOpen(true)} className="btn-primary text-xs" data-testid={`admin-quote-open-link-${q.id}`}>
              {q.quoted_amount ? "Resend payment link" : "Send payment link"}
            </button>
          )}
        </div>
      </div>
      {linkOpen && <PaymentLinkForm q={q} onCancel={() => setLinkOpen(false)} onDone={() => { setLinkOpen(false); onSaved(); }} />}
    </div>
  );
}

export function QuotesTab() {
  const [quotes, setQuotes] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const load = async () => {
    try { const { data } = await api.get("/admin/shop/quotes"); setQuotes(data); }
    catch (e) { toast.error(formatApiError(e)); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);
  const shown = filter === "all" ? quotes : quotes.filter((q) => q.status === filter);
  return (
    <div data-testid="admin-quotes">
      <div className="flex flex-wrap gap-2 mb-4">
        {["all", ...STATUSES].map((s) => (
          <button key={s} onClick={() => setFilter(s)} className={`tag ${filter === s ? "tag-active" : ""}`} data-testid={`admin-quotes-filter-${s}`}>
            {s.toUpperCase()} ({s === "all" ? quotes.length : quotes.filter((q) => q.status === s).length})
          </button>
        ))}
      </div>
      {loading ? <div className="font-mono text-xs text-[#52525B]">[LOADING]</div> : (
        <div className="space-y-2">
          {shown.length === 0 && <div className="surface p-6 text-center font-mono text-xs text-[#52525B]" data-testid="admin-quotes-empty">No quote requests.</div>}
          {shown.map((q) => <QuoteRow key={q.id} q={q} onSaved={load} />)}
        </div>
      )}
    </div>
  );
}
